/**
 * Activity tab — the scaling engine's paper trail: decisions, executed
 * actions, scale-up latency measurements, push-based unmet-demand
 * signals, and the drain reaper's live eligibility view. Read-only.
 */

import { useState } from 'react';
import styles from '../../routes/admin/pools/PoolDetail.module.css';
import {
  formatDuration,
  provisioningBadgeClass,
  relativeMs,
  type DemandSnapshot,
  type OrchestratorInfo,
  type PoolStats,
} from './shared';

type LogTab = 'decisions' | 'actions' | 'latency' | 'demand' | 'reaper';

/** Saved reaper tuning, mirrored from the pool's config draft. */
export interface ReaperSettingsView {
  enabled: boolean;
  idleGraceSeconds: number;
  bootImmunitySeconds: number;
  maxReapsPerTick: number;
}

/**
 * Mirror of the gateway's selectReapVictims eligibility checklist, evaluated
 * client-side per worker so operators can see exactly which condition holds
 * a reap back. Order matches the server: managed → pinned → state → drain →
 * sessions → idle grace → boot immunity.
 */
function reapVerdict(w: OrchestratorInfo, reaper: ReaperSettingsView | null): { label: string; eligible: boolean } {
  if (!reaper) return { label: 'config not loaded', eligible: false };
  if (!reaper.enabled) return { label: 'reaper disabled', eligible: false };
  if (!w.managed) return { label: 'blocked: unmanaged (external)', eligible: false };
  if (w.pinned) return { label: 'blocked: pinned', eligible: false };
  if (w.state === 'draining') return { label: 'blocked: draining', eligible: false };
  if (w.state !== 'available') return { label: `blocked: state=${w.state}`, eligible: false };
  if (w.activeSessions > 0) return { label: 'blocked: active sessions', eligible: false };
  if (!w.idleSince) return { label: 'blocked: idle clock not armed', eligible: false };
  const now = Date.now();
  const idleRemaining = reaper.idleGraceSeconds * 1000 - (now - Date.parse(w.idleSince));
  const immunityRemaining = reaper.bootImmunitySeconds * 1000 - (now - Date.parse(w.registeredAt));
  const wait = Math.max(idleRemaining, immunityRemaining);
  if (wait > 0) {
    const gate = idleRemaining >= immunityRemaining ? 'idle grace' : 'boot immunity';
    return { label: `eligible in ~${formatDuration(wait)} (${gate})`, eligible: false };
  }
  return { label: 'ELIGIBLE — reaped when floor allows, next tick', eligible: true };
}

export function ActivityTab({ stats, workers, reaper }: {
  stats: PoolStats | null;
  workers: OrchestratorInfo[];
  reaper: ReaperSettingsView | null;
}) {
  const [activeLogTab, setActiveLogTab] = useState<LogTab>('decisions');

  const hasAny = !!(stats?.scalingDecisions?.length || stats?.scaleActions?.length ||
    stats?.scaleUpLatency?.length || stats?.demandHistory?.length || workers.length);

  if (!hasAny) {
    return <div className={styles.empty}>No scaling activity recorded yet</div>;
  }

  // Latest mediated desired = the floor the reaper honors this tick.
  const latestTarget = stats?.scalingDecisions?.[0]?.targetSessionCapacity;
  const eligibleCount = workers.filter(w => reapVerdict(w, reaper).eligible).length;

  return (
    <div className={styles.section}>
      <div className={styles.tabBar}>
        <button
          className={activeLogTab === 'decisions' ? styles.tabButtonActive : styles.tabButton}
          aria-current={activeLogTab === 'decisions' ? 'true' : undefined}
          onClick={() => setActiveLogTab('decisions')}
        >
          Decisions{stats?.scalingDecisions?.length ? ` (${stats.scalingDecisions.length})` : ''}
        </button>
        <button
          className={activeLogTab === 'actions' ? styles.tabButtonActive : styles.tabButton}
          aria-current={activeLogTab === 'actions' ? 'true' : undefined}
          onClick={() => setActiveLogTab('actions')}
        >
          Actions{stats?.scaleActions?.length ? ` (${stats.scaleActions.length})` : ''}
        </button>
        <button
          className={activeLogTab === 'latency' ? styles.tabButtonActive : styles.tabButton}
          aria-current={activeLogTab === 'latency' ? 'true' : undefined}
          onClick={() => setActiveLogTab('latency')}
        >
          Latency{stats?.scaleUpLatency?.length ? ` (${stats.scaleUpLatency.length})` : ''}
        </button>
        <button
          className={activeLogTab === 'demand' ? styles.tabButtonActive : styles.tabButton}
          aria-current={activeLogTab === 'demand' ? 'true' : undefined}
          onClick={() => setActiveLogTab('demand')}
        >
          Demand{stats?.demandHistory?.length ? ` (${stats.demandHistory.length})` : ''}
        </button>
        <button
          className={activeLogTab === 'reaper' ? styles.tabButtonActive : styles.tabButton}
          aria-current={activeLogTab === 'reaper' ? 'true' : undefined}
          onClick={() => setActiveLogTab('reaper')}
        >
          Reaper{eligibleCount > 0 ? ` (${eligibleCount} eligible)` : ''}
        </button>
      </div>

      {/* Reaper Tab — live eligibility view. The reaper owns scale-down of
          connected capacity (engine decisions show Executed=N by design);
          this panel mirrors the server's per-victim checklist so the
          condition holding a reap back is visible, plus the pool-level
          gates that silently disable reaping entirely. */}
      {activeLogTab === 'reaper' && (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', margin: 'var(--space-3) 0', fontSize: 'var(--text-xs)' }}>
            <span>
              <strong>Reaper:</strong>{' '}
              {reaper?.enabled ? 'enabled' : 'DISABLED'}
            </span>
            <span><strong>Idle grace:</strong> {reaper ? `${reaper.idleGraceSeconds}s` : '—'}</span>
            <span><strong>Boot immunity:</strong> {reaper ? `${reaper.bootImmunitySeconds}s` : '—'}</span>
            <span><strong>Max/tick:</strong> {reaper ? reaper.maxReapsPerTick : '—'}</span>
            <span><strong>Floor (mediated desired):</strong> {latestTarget ?? '—'} slots</span>
          </div>
          {(stats?.override?.autoscalingPaused || stats?.override?.scaling) && (
            <div style={{
              margin: '0 0 var(--space-3)',
              padding: 'var(--space-2) var(--space-3)',
              border: 'var(--border-width) solid var(--color-error)',
              color: 'var(--color-error)',
              fontSize: 'var(--text-xs)',
            }}>
              {stats?.override?.autoscalingPaused && (
                <div>Autoscaling is PAUSED{stats.override.pauseReason ? ` (${stats.override.pauseReason})` : ''} — the reaper does not run while paused.</div>
              )}
              {stats?.override?.scaling && (
                <div>
                  A manual scale override is active (desired={stats.override.scaling.desiredCapacity}, set {relativeMs(stats.override.scaling.createdAt)}) —
                  the reaper does not run while a manual override pins capacity. Clear it from the Overview tab to resume reaping.
                </div>
              )}
            </div>
          )}
          {workers.length === 0 ? (
            <div className={styles.empty}>No connected workers</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Worker</th>
                  <th>State</th>
                  <th>Sessions</th>
                  <th>Idle</th>
                  <th>Connected</th>
                  <th>Flags</th>
                  <th>Reap verdict</th>
                </tr>
              </thead>
              <tbody>
                {workers.map(w => {
                  const v = reapVerdict(w, reaper);
                  return (
                    <tr key={w.workerId}>
                      <td>{w.workerId}</td>
                      <td><span className={provisioningBadgeClass(w.state === 'available' ? 'provisioned' : 'pending')}>{w.state}</span></td>
                      <td>{w.activeSessions}/{w.maxConcurrentSessions}</td>
                      <td>{w.idleSince ? formatDuration(Date.now() - Date.parse(w.idleSince)) : '-'}</td>
                      <td title={w.registeredAt}>{formatDuration(Date.now() - Date.parse(w.registeredAt))}</td>
                      <td>
                        {!w.managed && 'EXT '}
                        {w.pinned && 'PIN'}
                      </td>
                      <td style={{ fontSize: 'var(--text-xs)', color: v.eligible ? 'var(--color-success)' : undefined }}>
                        {v.label}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* Decisions Tab */}
      {activeLogTab === 'decisions' && stats?.scalingDecisions && stats.scalingDecisions.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Time</th>
              <th>Action</th>
              <th>Reason</th>
              <th>Current</th>
              <th>Target</th>
              <th>Slots (used/total)</th>
              <th>Unmet</th>
              <th>Executed</th>
            </tr>
          </thead>
          <tbody>
            {stats.scalingDecisions.slice(0, 25).map(d => (
              <tr key={d.id}>
                <td title={new Date(d.at).toISOString()}>{relativeMs(d.at)}</td>
                <td>
                  <span className={
                    d.action === 'scale-up' ? styles.provProvisioned :
                    d.action === 'scale-down' ? styles.provFailed :
                    styles.provPending
                  }>
                    {d.action}
                  </span>
                </td>
                <td style={{ fontSize: 'var(--text-xs)', maxWidth: 400, wordBreak: 'break-word' }} title={d.reason}>
                  {d.reason}
                  {d.activeScheduleWindow && (
                    <div style={{ color: 'var(--text-tertiary)', marginTop: 'var(--space-1)' }}>
                      Schedule: {d.activeScheduleWindow}
                    </div>
                  )}
                </td>
                <td>{d.currentSessionCapacity}</td>
                <td>{d.targetSessionCapacity}</td>
                <td>{d.usedSessionSlots}/{d.totalSessionSlots}</td>
                <td>{d.demandUnmet ?? '-'}</td>
                <td>{d.executed ? 'Y' : 'N'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Actions Tab */}
      {activeLogTab === 'actions' && stats?.scaleActions && stats.scaleActions.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Time</th>
              <th>Direction</th>
              <th>From (machines)</th>
              <th>To (machines)</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {stats.scaleActions.slice(0, 15).map((a, i) => (
              <tr key={i}>
                <td title={new Date(a.at).toISOString()}>{relativeMs(a.at)}</td>
                <td>
                  <span className={a.direction === 'up' ? styles.provProvisioned : styles.provFailed}>
                    {a.direction}
                  </span>
                </td>
                <td>{a.fromInstanceCount}</td>
                <td>{a.toInstanceCount}</td>
                <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Latency Tab */}
      {activeLogTab === 'latency' && stats?.scaleUpLatency && stats.scaleUpLatency.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Requested</th>
              <th>From (machines)</th>
              <th>To (machines)</th>
              <th>Time to First</th>
              <th>Time to Target</th>
              <th>Status</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {stats.scaleUpLatency.map(l => (
              <tr key={l.id}>
                <td title={new Date(l.requestedAt).toISOString()}>{relativeMs(l.requestedAt)}</td>
                <td>{l.fromWorkers}</td>
                <td>{l.toWorkers}</td>
                <td>{formatDuration(l.timeToFirstWorkerMs)}</td>
                <td>{formatDuration(l.timeToTargetMs)}</td>
                <td>
                  <span className={provisioningBadgeClass(
                    l.status === 'completed' ? 'provisioned' :
                    l.status === 'failed' || l.status === 'timed_out' ? 'failed' : 'pending'
                  )}>
                    {l.status}
                  </span>
                </td>
                <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Demand Tab — push-based unmet-demand signals grouped by app:version.
          Each row is one incoming push from a marketplace that couldn't
          place a job against direct workers or mux virtual agents. */}
      {activeLogTab === 'demand' && stats?.demandHistory && stats.demandHistory.length > 0 && (
        <DemandLog demandHistory={stats.demandHistory} />
      )}

      {/* Empty state for selected tab */}
      {activeLogTab === 'decisions' && (!stats?.scalingDecisions || stats.scalingDecisions.length === 0) && (
        <div className={styles.empty}>No scaling decisions recorded</div>
      )}
      {activeLogTab === 'actions' && (!stats?.scaleActions || stats.scaleActions.length === 0) && (
        <div className={styles.empty}>No scale actions recorded</div>
      )}
      {activeLogTab === 'latency' && (!stats?.scaleUpLatency || stats.scaleUpLatency.length === 0) && (
        <div className={styles.empty}>No scale-up latency data</div>
      )}
      {activeLogTab === 'demand' && (!stats?.demandHistory || stats.demandHistory.length === 0) && (
        <div className={styles.empty}>No demand history</div>
      )}
    </div>
  );
}

function DemandLog({ demandHistory }: { demandHistory: DemandSnapshot[] }) {
  // Group by app:version (falling back to "unattributed" for legacy
  // polled rows that pre-date the push refactor).
  const grouped = new Map<string, DemandSnapshot[]>();
  for (const d of demandHistory) {
    const key = d.appId && d.versionId ? `${d.appId}:${d.versionId}` : 'unattributed';
    const arr = grouped.get(key) ?? [];
    arr.push(d);
    grouped.set(key, arr);
  }
  // Sort groups by most-recent push descending so active app:versions float
  // to the top.
  const orderedGroups = [...grouped.entries()]
    .map(([key, rows]) => {
      const latest = Math.max(...rows.map((r) => r.fetchedAt));
      return [key, rows, latest] as const;
    })
    .sort((a, b) => b[2] - a[2]);

  // Top-level rollup: fleet-wide total across all app:version groups.
  const fleetTotal = demandHistory.length;
  const fleetPeakUnmet = demandHistory.reduce((m, d) => Math.max(m, d.totalUnmet), 0);
  const fleetFirst = Math.min(...demandHistory.map((d) => d.fetchedAt));
  const fleetLatest = Math.max(...demandHistory.map((d) => d.fetchedAt));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-xs)',
        color: 'var(--color-text-secondary)',
        padding: 'var(--space-2) var(--space-3)',
        background: 'var(--color-bg-secondary)',
        border: 'var(--border-width) solid var(--color-border)',
      }}>
        <span style={{
          fontWeight: 'var(--weight-semibold)' as any,
          color: 'var(--color-text-primary)',
          marginRight: 'var(--space-3)',
        }}>
          {fleetTotal} push{fleetTotal === 1 ? '' : 'es'} across {orderedGroups.length} app:version{orderedGroups.length === 1 ? '' : 's'}
        </span>
        peak unmet {fleetPeakUnmet} · span {relativeMs(fleetFirst)} → {relativeMs(fleetLatest)}
      </div>

      {orderedGroups.map(([key, rows]) => {
        const sorted = [...rows].sort((a, b) => b.fetchedAt - a.fetchedAt);
        const totalPushes = rows.length;
        const latestJobCount = sorted[0]?.totalUnmet ?? 0;
        const peakUnmet = rows.reduce((m, r) => Math.max(m, r.totalUnmet), 0);
        const firstAt = Math.min(...rows.map((r) => r.fetchedAt));
        const latestAt = Math.max(...rows.map((r) => r.fetchedAt));
        return (
          <div key={key}>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xs)',
              letterSpacing: 'var(--tracking-wide)',
              textTransform: 'uppercase',
              color: 'var(--color-text-tertiary)',
              marginBottom: 'var(--space-2)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-3)',
              alignItems: 'baseline',
            }}>
              <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--weight-semibold)' as any }}>{key}</span>
              <span>{totalPushes} push{totalPushes === 1 ? '' : 'es'}</span>
              <span>latest {latestJobCount} unmet · {relativeMs(latestAt)}</span>
              <span>peak {peakUnmet}</span>
              <span>first {relativeMs(firstAt)}</span>
            </div>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '6rem' }}>Time</th>
                  <th style={{ width: '5rem' }}>Unmet</th>
                  <th style={{ width: '5rem' }}>Total</th>
                  <th>Relative</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((d, i) => {
                  const totalMp =
                    Array.isArray(d.cells) && d.cells[0] && 'totalUnmatchedMarketplace' in d.cells[0]
                      ? (d.cells[0] as unknown as { totalUnmatchedMarketplace: number }).totalUnmatchedMarketplace
                      : null;
                  const barWidth = peakUnmet > 0 ? Math.max(2, Math.round((d.totalUnmet / peakUnmet) * 100)) : 0;
                  return (
                    <tr key={i}>
                      <td title={new Date(d.fetchedAt).toISOString()} style={{ fontFamily: 'var(--font-mono)' }}>
                        {relativeMs(d.fetchedAt)}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' as const }}>{d.totalUnmet}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' as const, color: 'var(--color-text-tertiary)' }}>
                        {totalMp ?? '-'}
                      </td>
                      <td>
                        <div
                          aria-hidden="true"
                          style={{
                            background: 'var(--color-accent)',
                            opacity: 0.7,
                            height: '10px',
                            width: `${barWidth}%`,
                            minWidth: barWidth > 0 ? '2px' : '0',
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
